import { ArrowForward, DeleteForever } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import CButton from '../../../../components/CButton';
import CDeleteConfirmationButton from '../../../../components/CDeleteConfirmationButton';
import CLocalizedText from '../../../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../../components/CPageModule';
import CRadioGroup from '../../../../components/CRadioGroup';
import CResponsiveGrid from '../../../../components/CResponsiveGrid';
import CTextFieldDisplay from '../../../../components/CTextFieldDisplay';
import NotFoundModule from '../../../../components/PageGates/NotFoundModule';
import PageModuleFormGroup from '../../../../components/PageModuleFormGroup';
import PageModuleFormRow from '../../../../components/PageModuleFormRow';
import TagButton from '../../../../components/TagButton';
import Page from '../../../../components/layout/Page';
import { CAccountRequestRoles } from '../../../../constants/CAccountRequestRoles';
import { BreakpointsInPx } from '../../../../enums/BreakpointsInPx';
import { ESignUpStatus, type ESignUpStatusValue } from '../../../../enums/ESignUpStatus';
import { ETonalButtonClassNames } from '../../../../enums/ETagButtonColors';
import { type EUserRoles } from '../../../../enums/EUserRoles';
import { PageRoutes } from '../../../../enums/enums';
import HGeneric from '../../../../helpers/HGeneric';
import { updateStateAttribute } from '../../../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import type { IRequestState } from '../../../../models/IRequestState';
import type { ISignUpRequestDetail } from '../../../../models/SignUpRequestDetail';
import SvUserManagementCoreService from '../../../../services/SvUserManagementCoreService';
import { updateSnackbar } from '../../../../store/appSlice';

const updateOptions = {
  opt1: {
    label: <CLocalizedText dictKey={'signupReq.radio.approve.label'} />,
    description: <CLocalizedText dictKey={'signupReq.radio.approve.desc'} />,
    value: ESignUpStatus.APPROVED as ESignUpStatusValue
  },
  opt2: {
    label: <CLocalizedText dictKey={'signupReq.radio.reject.label'} />,
    description: <CLocalizedText dictKey={'signupReq.radio.reject.desc'} />,
    value: ESignUpStatus.REJECTED as ESignUpStatusValue
  }
} as const;

type UpdateOptionKey = keyof typeof updateOptions;

interface State {
  requestState: IRequestState;
  requestStateForUpdate: IRequestState;
  requestStateForDelete: IRequestState;
  inquiryNotFound: boolean;
  detail?: ISignUpRequestDetail;
  updateOption: UpdateOptionKey | null;
  roleOption: EUserRoles | null;
}

export default function PSingleSignUpRequest() {
  const { id } = useParams();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { dashboardOutletDimensions, locale } = useAppSelector((state) => state.dashboard);
  const isMobile = dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_1;
  const gridColsStyle = isMobile ? 'repeat(1, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))';

  const [state, setState] = useState<State>({
    requestState: { isLoading: false, isError: false },
    requestStateForUpdate: { isLoading: false, isError: false },
    requestStateForDelete: { isLoading: false, isError: false },
    inquiryNotFound: false,
    detail: undefined,
    updateOption: null,
    roleOption: null
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const labelSignUpRequest = CLocalizedTextStringStatic('signupReq.heading.single');
  const deleteModalTitle = CLocalizedTextStringStatic('signupReq.delete.modal.title');

  useEffect(() => {
    if (!id) return;

    (async () => {
      updateStateAttribute<State>(
        {
          requestState: { isLoading: true, isError: false },
          inquiryNotFound: false
        },
        setState
      );

      const res = await SvUserManagementCoreService.getSingleRequest(id);
      const isSuccess = await res.isSuccess();

      if (!isSuccess) {
        updateStateAttribute<State>(
          {
            requestState: { isLoading: false, isError: true },
            inquiryNotFound: true
          },
          setState
        );
        return;
      }

      const data = await res.getData();

      let defaultOption: UpdateOptionKey | null;

      if (data?.status === ESignUpStatus.APPROVED) {
        defaultOption = 'opt1';
      } else if (data?.status === ESignUpStatus.REJECTED) {
        defaultOption = 'opt2';
      } else {
        defaultOption = null;
      }

      updateStateAttribute<State>(
        {
          requestState: { isLoading: false, isError: false },
          inquiryNotFound: false,
          detail: data,
          updateOption: defaultOption,
          roleOption: data?.assignedRole ?? data?.requestedRole ?? null
        },
        setState
      );
    })();
  }, [id, locale]);

  const handleUpdate = async () => {
    if (!id || !state.detail || !state.updateOption) return;

    updateStateAttribute<State>(
      {
        requestStateForUpdate: { isLoading: true, isError: false }
      },
      setState
    );

    const res = await SvUserManagementCoreService.updateSignUpRequest(id, {
      status: updateOptions[state.updateOption].value,
      role: state.roleOption ?? undefined
    });

    const isSuccess = await res.isSuccess();
    const message = await res.getMessage();

    if (!isSuccess) {
      updateStateAttribute<State>(
        {
          requestStateForUpdate: { isLoading: false, isError: true, objError: await res.getDetail() }
        },
        setState
      );

      dispatch(
        updateSnackbar({
          message,
          severity: 'error',
          isVisible: true
        })
      );

      return;
    }

    const updatedDetail = await res.getData();

    let updatedOption: UpdateOptionKey | null;

    if (updatedDetail?.status === ESignUpStatus.APPROVED) {
      updatedOption = 'opt1';
    } else if (updatedDetail?.status === ESignUpStatus.REJECTED) {
      updatedOption = 'opt2';
    } else {
      updatedOption = null;
    }

    updateStateAttribute<State>(
      {
        requestStateForUpdate: { isLoading: false, isError: false },
        detail: updatedDetail,
        updateOption: updatedOption,
        roleOption: updatedDetail?.assignedRole ?? updatedDetail?.requestedRole ?? null
      },
      setState
    );

    dispatch(
      updateSnackbar({
        message,
        severity: 'success',
        isVisible: true
      })
    );
  };

  const handleDelete = async () => {
    if (!id || !state.detail) return;

    updateStateAttribute<State>(
      {
        requestStateForDelete: { isLoading: true, isError: false }
      },
      setState
    );

    const res = await SvUserManagementCoreService.deleteSignUpRequest(id);
    const isSuccess = await res.isSuccess();
    const message = await res.getMessage();

    if (!isSuccess) {
      updateStateAttribute<State>(
        {
          requestStateForDelete: { isLoading: false, isError: true }
        },
        setState
      );

      dispatch(
        updateSnackbar({
          message,
          severity: 'error',
          isVisible: true
        })
      );

      return;
    }

    updateStateAttribute<State>(
      {
        requestStateForDelete: { isLoading: false, isError: false },
        detail: undefined
      },
      setState
    );

    dispatch(
      updateSnackbar({
        message,
        severity: 'success',
        isVisible: true
      })
    );
    navigate(`/${PageRoutes.USERS_OUTLET}/${PageRoutes.SIGN_UP_REQUESTS_OUTLET}`);
  };

  const pageTitle = state.detail ? [state.detail.firstName, state.detail.lastName].join(' ').trim() : (id as string);

  return (
    <>
      {state.inquiryNotFound && <NotFoundModule />}
      {!state.inquiryNotFound && (
        <Page title={pageTitle}>
          <CPageModule fullWidth apiRequestState={state.requestState} showContent={!!state.detail}>
            <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
              <h2>{labelSignUpRequest}</h2>
            </CPageModule>

            <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
              <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                <h3>
                  <CLocalizedText dictKey={'signupReq.subheading.personalDetails'} />
                </h3>
              </CPageModule>

              <CPageModule submodule>
                <CResponsiveGrid maxColumns={2} style={{ columnGap: '1.5rem', rowGap: '1.5rem' }}>
                  <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
                    <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.firstName'} />}>
                      {state.detail?.firstName}
                    </CTextFieldDisplay>

                    <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.lastName'} />}>
                      {state.detail?.lastName}
                    </CTextFieldDisplay>

                    <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.email'} />}>
                      {state.detail?.email}
                    </CTextFieldDisplay>
                  </CPageModule>

                  <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
                    <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.company'} />}>
                      {state.detail?.company ?? '-'}
                    </CTextFieldDisplay>

                    <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.position'} />}>
                      {state.detail?.position?.trim() ? (
                        state.detail.position
                      ) : (
                        <CLocalizedText dictKey={'global.notAvailable'} />
                      )}
                    </CTextFieldDisplay>
                  </CPageModule>
                </CResponsiveGrid>
              </CPageModule>
            </CPageModule>

            <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
              <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                <h3>
                  <CLocalizedText dictKey={'signupReq.subheading.meta'} />
                </h3>
              </CPageModule>

              <CPageModule submodule>
                <div className={'grid gap-4'} style={{ gridTemplateColumns: gridColsStyle }}>
                  <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.status'} />}>
                    {state.detail?.statusLabel ?? '-'}
                  </CTextFieldDisplay>

                  <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.createdTs'} />}>
                    {HGeneric.formatString(state.detail?.createdAt, {
                      in: 'tzTimestamp',
                      out: 'localTimestampExtended'
                    })}
                  </CTextFieldDisplay>

                  <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.reviewedBy'} />}>
                    {state.detail?.updatedBy?.fullName ?? '-'}
                  </CTextFieldDisplay>

                  <CTextFieldDisplay label={<CLocalizedText dictKey={'signupReq.label.updatedTs'} />}>
                    {HGeneric.formatString(state.detail?.updatedAt, {
                      in: 'tzTimestamp',
                      out: 'localTimestampExtended'
                    })}
                  </CTextFieldDisplay>
                </div>
              </CPageModule>
            </CPageModule>

            <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
              <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                <h3>
                  <CLocalizedText dictKey={'signupReq.subheading.accountType'} />
                </h3>
              </CPageModule>

              <CPageModule submodule>
                <PageModuleFormGroup>
                  <PageModuleFormRow>
                    <CRadioGroup
                      options={Object.fromEntries(
                        Object.entries(CAccountRequestRoles).map(([role, option]) => [
                          role,
                          {
                            ...option,
                            label: (
                              <span className={'inline-flex flex-wrap items-center gap-2'}>
                                {option.label}
                                {state.detail?.requestedRole === role && (
                                  <TagButton>
                                    <CLocalizedText dictKey={'signupReq.tag.requested'} />
                                  </TagButton>
                                )}
                                {state.detail?.assignedRole === role && (
                                  <TagButton color={ETonalButtonClassNames.TEAL_GREEN_1}>
                                    <CLocalizedText dictKey={'signupReq.tag.assigned'} />
                                  </TagButton>
                                )}
                              </span>
                            )
                          }
                        ])
                      ) as typeof CAccountRequestRoles}
                      value={state.roleOption as keyof typeof CAccountRequestRoles | null}
                      error={(state.requestStateForUpdate.objError?.role?.length ?? 0) > 0}
                      helperText={state.requestStateForUpdate.objError?.role}
                      disabled={state.detail?.status !== ESignUpStatus.CREATED}
                      onChange={(key) => {
                        updateStateAttribute<State>({ roleOption: key }, setState);
                      }}
                    />
                  </PageModuleFormRow>
                </PageModuleFormGroup>
              </CPageModule>
            </CPageModule>

            <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
              <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                <h3>
                  <CLocalizedText dictKey={'signupReq.subheading.process'} />
                </h3>
              </CPageModule>

              <CPageModule submodule>
                <PageModuleFormGroup>
                  <PageModuleFormRow>
                    <CRadioGroup
                      options={updateOptions}
                      value={state.updateOption}
                      disabled={state.detail?.status !== ESignUpStatus.CREATED}
                      onChange={(key) => {
                        updateStateAttribute<State>({ updateOption: key }, setState);
                      }}
                    />
                  </PageModuleFormRow>
                  <PageModuleFormRow description={<CLocalizedText dictKey={'signupReq.update.btn.desc'} />}>
                    <CButton
                      style={{
                        borderWidth: 1,
                        width: '100%',
                        justifyContent: 'center'
                      }}
                      icons={{ end: <ArrowForward /> }}
                      onClick={handleUpdate}
                      state={state.requestStateForUpdate}
                      disabled={
                        state.detail?.status !== ESignUpStatus.CREATED ||
                        !state.updateOption ||
                        state.requestStateForUpdate.isLoading
                      }
                      isForm
                    >
                      <CLocalizedText dictKey={'signupReq.update.btn'} />
                    </CButton>
                  </PageModuleFormRow>
                </PageModuleFormGroup>
              </CPageModule>
            </CPageModule>

            <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
              <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                <h3>
                  <CLocalizedText dictKey={'signupReq.delete.btn'} />
                </h3>
              </CPageModule>

              <CPageModule submodule>
                <PageModuleFormRow>
                  <CButton
                    style={{
                      borderWidth: 1,
                      width: '100%',
                      justifyContent: 'center'
                    }}
                    icons={{ start: <DeleteForever /> }}
                    variant={'danger'}
                    onClick={() => {
                      setShowDeleteModal(true);
                    }}
                    state={state.requestStateForDelete}
                    isForm
                    disabled={state.requestStateForDelete.isLoading}
                  >
                    <CLocalizedText dictKey={'signupReq.delete.btn'} />
                  </CButton>
                </PageModuleFormRow>
              </CPageModule>

              {showDeleteModal && (
                <CDeleteConfirmationButton
                  showModal={showDeleteModal}
                  onConfirm={async () => {
                    await handleDelete();
                    setShowDeleteModal(false);
                  }}
                  onCancel={() => {
                    setShowDeleteModal(false);
                  }}
                  title={deleteModalTitle}
                />
              )}
            </CPageModule>
          </CPageModule>
        </Page>
      )}
    </>
  );
}
